/**
 * Typed client for the catalog dashboard API (ADR-0103 — "make the
 * catalog usable + visible"). Backs the per-site Catalog view. Mirrors
 * the `site-settlement-rail.ts` / `account-settlement-rail.ts` idiom:
 * each call resolves to a discriminated-union result keyed on `kind`,
 * never throws, and the caller branches on `kind`.
 *
 * Four endpoints, all owner/admin-only (server 403 → `forbidden`),
 * site-scoped (a foreign / missing site → 404 → `not_found`):
 *
 *   POST /v1/sites/:site_id/catalog/import   — raw `text/csv` body, 8 MB
 *       cap. Returns `{ synced, stale, errors, truncated }`. The body IS
 *       the CSV text (sent via `HttpClient.requestText`), not a JSON
 *       envelope. 413 → `too_large`; 415 → `unsupported_type`.
 *   GET  /v1/sites/:site_id/catalog           — bounded list
 *       (`?limit&offset`). Returns `{ products, limit, offset, has_more }`.
 *   GET  /v1/sites/:site_id/compliance        — read the site's
 *       compliance fields (omitted when never captured).
 *   PUT  /v1/sites/:site_id/compliance        — partial update; https-only
 *       URL validation echoes the server's 400 `reason`
 *       (`invalid_seller_tos_url`, …) as `bad_request`.
 *
 * Wire shape is snake_case (CLAUDE.md §3); money is minor-unit integers
 * (`price_minor`) — formatted for DISPLAY only by the view, never math'd.
 */

import type { CatalogProduct, Rule } from '@sill/types';
import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

/* ---------- shared shapes ---------- */

export interface CatalogImportSummary {
  readonly synced: number;
  readonly stale: number;
  readonly errors: number;
  readonly truncated: boolean;
  /** Rows skipped because their SKU already belongs to a live cart product
   *  (cross-source dedup). Absent on older API builds → treated as 0. */
  readonly deduped: number;
}

export interface CatalogPage {
  readonly products: readonly CatalogProduct[];
  readonly limit: number;
  readonly offset: number;
  readonly has_more: boolean;
  /**
   * Site-level freshness: the newest `last_synced_at` across the site's
   * non-removed catalog (ISO-8601), or `null` when the site has never
   * synced. Independent of the current page/filter. Older API builds omit
   * it; absent → `null`.
   */
  readonly last_synced_at: string | null;
}

/** The wire-accepted `?sort` columns (mirrors the server whitelist). */
export type CatalogSortColumn =
  | 'title'
  | 'brand'
  | 'price_minor'
  | 'availability'
  | 'inventory_quantity'
  | 'sync_state'
  | 'is_eligible_search'
  | 'is_eligible_checkout'
  | 'last_synced_at';

/** The two accepted sort directions. */
export type CatalogSortDir = 'asc' | 'desc';

/**
 * Site compliance fields (ADR-0103 §4 checkout-gated capture). Optional
 * fields are absent when the server has not captured them yet — the GET
 * stub for a never-captured site returns just `{ site_id, account_id }`.
 */
export interface SiteComplianceFields {
  readonly site_id: string;
  readonly account_id: string;
  readonly seller_tos_url?: string;
  readonly seller_privacy_policy_url?: string;
  readonly returns_policy?: string;
}

/** The three writable compliance fields. `null` clears, `undefined`
 *  leaves unchanged (the server upsert does the partial merge). */
export interface ComplianceUpdate {
  readonly seller_tos_url?: string | null;
  readonly seller_privacy_policy_url?: string | null;
  readonly returns_policy?: string | null;
}

/**
 * Catalog eligibility counts (ADR-0103 "suggested → review → accept"). The
 * persisted eligibility of the site's live catalog: how many products are
 * search-discoverable + checkout-eligible out of the total.
 */
export interface EligibilityCounts {
  readonly total: number;
  readonly search_eligible: number;
  readonly checkout_eligible: number;
}

/**
 * The two site-level CHECKOUT gates (ADR-0103 §4). Search-eligibility does
 * NOT depend on these; checkout-eligibility additionally requires a connected
 * settlement rail + the captured compliance fields. The view surfaces a hint
 * when either is false.
 */
export interface EligibilitySiteContext {
  readonly has_settlement_rail: boolean;
  readonly has_compliance_fields: boolean;
}

/**
 * GET eligibility payload: the merchant's CURRENT catalog rules (empty when
 * not configured), the SUGGESTED default rule set, the current counts, and
 * the resolved site context. `current_rules` / `suggestion` are `Rule[]`
 * round-tripped opaquely back into preview/activate — the view never edits
 * them (full rule editing is a later slice).
 */
export interface EligibilityState {
  readonly current_rules: readonly Rule[];
  readonly suggestion: readonly Rule[];
  /** ADR-0112 §4 — search + CHECKOUT rule set; activate this to make in-stock
   *  products buyable (also needs a connected rail + compliance). */
  readonly checkout_suggestion: readonly Rule[];
  readonly counts: EligibilityCounts;
  readonly site_context: EligibilitySiteContext;
}

/** Dry-run preview of a candidate rule set over the live catalog. */
export interface EligibilityPreview {
  readonly total: number;
  readonly would_search_eligible: number;
  readonly would_checkout_eligible: number;
}

/** Result of activating a rule set: the new policy version + fresh counts. */
export interface EligibilityActivation {
  readonly policy_version: string;
  readonly counts: EligibilityCounts;
}

/* ---------- result unions ---------- */

export type ImportCatalogResult =
  | { readonly kind: 'ok'; readonly summary: CatalogImportSummary }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'too_large' }
  | { readonly kind: 'unsupported_type' }
  | { readonly kind: 'bad_request'; readonly reason?: string }
  | { readonly kind: 'error'; readonly message: string };

/**
 * ADR-0103 manual "Sync from Shopify now" trigger result. The server
 * responds 202 `{status:'started'}` on accept; 429 when rate-limited (with
 * a `reason` distinguishing the per-account DAILY CAP from the per-site
 * DEBOUNCE), 409 when a sync is already running for the account, 403 when
 * not owner/admin, 404 for a foreign/missing site, 503 when the connector
 * KMS is unavailable.
 */
export type SyncNowResult =
  | { readonly kind: 'started' }
  | { readonly kind: 'rate_limited'; readonly reason: 'daily_cap' | 'too_soon' | 'sync_busy' | 'unknown' }
  | { readonly kind: 'in_progress' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'error'; readonly message: string };

export type GetCatalogResult =
  | { readonly kind: 'ok'; readonly page: CatalogPage }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

/** The merchant-editable fields for a manually-managed catalog product
 *  (PATCH body). The form sends the FULL set; `title` is required. */
export interface CatalogProductEdit {
  readonly title: string;
  readonly sku?: string | null;
  readonly brand?: string | null;
  readonly description?: string | null;
  readonly image_url?: string | null;
  readonly url?: string | null;
  readonly price_minor?: number | null;
  readonly currency?: string | null;
  readonly availability?: string | null;
  readonly inventory_quantity?: number | null;
}

export type UpdateProductResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  /** The product is owned by a connected cart (Shopify/…) — edit it there. */
  | { readonly kind: 'source_managed' }
  | { readonly kind: 'bad_request'; readonly reason?: string }
  | { readonly kind: 'error'; readonly message: string };

export type DeleteProductResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export type GetComplianceResult =
  | { readonly kind: 'ok'; readonly compliance: SiteComplianceFields }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export type PutComplianceResult =
  | { readonly kind: 'ok'; readonly compliance: SiteComplianceFields }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'bad_request'; readonly reason?: string }
  | { readonly kind: 'error'; readonly message: string };

export type GetEligibilityResult =
  | { readonly kind: 'ok'; readonly state: EligibilityState }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export type PreviewEligibilityResult =
  | { readonly kind: 'ok'; readonly preview: EligibilityPreview }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'bad_request'; readonly reason?: string }
  | { readonly kind: 'error'; readonly message: string };

/**
 * Activate result. The activate endpoint MUTATES policy server-side; a 409
 * `conflict` means a concurrent policy change lost the race for the single
 * active-policy slot — surfaced so the view can ask the merchant to refresh
 * and retry.
 */
export type ActivateEligibilityResult =
  | { readonly kind: 'ok'; readonly activation: EligibilityActivation }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'conflict' }
  | { readonly kind: 'bad_request'; readonly reason?: string }
  | { readonly kind: 'error'; readonly message: string };

/* ---------- internal wire shapes ---------- */

interface ImportWire {
  readonly synced?: unknown;
  readonly stale?: unknown;
  readonly errors?: unknown;
  readonly truncated?: unknown;
  readonly deduped?: unknown;
}

interface CatalogWire {
  readonly products?: unknown;
  readonly limit?: unknown;
  readonly offset?: unknown;
  readonly has_more?: unknown;
  readonly last_synced_at?: unknown;
}

interface ComplianceWire {
  readonly site_id?: unknown;
  readonly account_id?: unknown;
  readonly seller_tos_url?: unknown;
  readonly seller_privacy_policy_url?: unknown;
  readonly returns_policy?: unknown;
}

interface EligibilityCountsWire {
  readonly total?: unknown;
  readonly search_eligible?: unknown;
  readonly checkout_eligible?: unknown;
}

interface EligibilityWire {
  readonly current_rules?: unknown;
  readonly suggestion?: unknown;
  readonly checkout_suggestion?: unknown;
  readonly counts?: unknown;
  readonly site_context?: unknown;
}

interface SiteContextWire {
  readonly has_settlement_rail?: unknown;
  readonly has_compliance_fields?: unknown;
}

interface PreviewWire {
  readonly total?: unknown;
  readonly would_search_eligible?: unknown;
  readonly would_checkout_eligible?: unknown;
}

interface ActivationWire {
  readonly policy_version?: unknown;
  readonly counts?: unknown;
}

/* --------------------------------------------------------------------- */

const MAX_PAGE_LIMIT = 500;

/**
 * POST a CSV text body to the import endpoint. The CSV string is sent
 * verbatim as `text/csv` via `requestText`; the server parses it and
 * returns the sync summary.
 */
export async function importCatalogCsv(
  http: HttpClient,
  site_id: string,
  csv: string,
): Promise<ImportCatalogResult> {
  // `requestText` is optional on the interface (test stubs may omit it);
  // production always has it. A stub without it is a test wiring gap.
  if (typeof http.requestText !== 'function') {
    return { kind: 'error', message: 'CSV upload is not available in this build.' };
  }
  try {
    const res = await http.requestText<ImportWire>({
      method: 'POST',
      path: `/v1/sites/${encodeURIComponent(site_id)}/catalog/import`,
      text_body: csv,
      text_content_type: 'text/csv',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const summary = parseImportSummary(res);
    if (summary === null) {
      return { kind: 'error', message: 'Server returned an unexpected import summary.' };
    }
    return { kind: 'ok', summary };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'payload_too_large') return { kind: 'too_large' };
      if (err.kind === 'unsupported_media_type') return { kind: 'unsupported_type' };
      if (err.kind === 'bad_request') {
        return err.server_reason !== undefined
          ? { kind: 'bad_request', reason: err.server_reason }
          : { kind: 'bad_request' };
      }
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/**
 * POST the manual "Sync from Shopify now" trigger. The server records the
 * paid-call attempt + responds 202 immediately, running the bounded sync in
 * the background. Maps every outcome to a `SyncNowResult` kind; never throws.
 *
 * Status mapping note: the HttpClient's `request()` treats only 200/201/204
 * as success, so the 202 surfaces as a thrown `ApiError(server_error, 202)`
 * — we detect `err.status === 202` and map it to `started`. A 429 is thrown
 * by the client as `AuthRateLimitError` (the shared rate-limit class), which
 * now carries the body `server_reason` (`daily_cap` | `too_soon`); we branch
 * on that BEFORE the `ApiError` checks and surface the reason to the view.
 */
export async function syncNow(
  http: HttpClient,
  site_id: string,
): Promise<SyncNowResult> {
  try {
    await http.request<{ status?: unknown }>({
      method: 'POST',
      path: `/v1/sites/${encodeURIComponent(site_id)}/catalog/sync`,
    });
    // A 200/201/204 here is unexpected (the route returns 202), but treat any
    // 2xx that did NOT throw as a successful start.
    return { kind: 'started' };
  } catch (err) {
    // 429 — the HttpClient throws the shared AuthRateLimitError. It now
    // carries the body `server_reason` so we can distinguish the per-account
    // daily cap from the per-site debounce.
    if (err instanceof AuthRateLimitError) {
      const reason =
        err.server_reason === 'daily_cap'
        || err.server_reason === 'too_soon'
        || err.server_reason === 'sync_busy'
          ? err.server_reason
          : 'unknown';
      return { kind: 'rate_limited', reason };
    }
    if (err instanceof ApiError) {
      // 202 accepted — surfaced as a non-success status by the JSON client.
      if (err.status === 202) return { kind: 'started' };
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'conflict') return { kind: 'in_progress' };
      if (err.kind === 'rate_limited') {
        const reason =
          err.server_reason === 'daily_cap'
          || err.server_reason === 'too_soon'
          || err.server_reason === 'sync_busy'
            ? err.server_reason
            : 'unknown';
        return { kind: 'rate_limited', reason };
      }
      if (err.kind === 'service_unavailable') return { kind: 'unavailable' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/**
 * Probe whether the site has a CONNECTED Shopify integration, so the catalog
 * view can gate the Shopify-only "Sync from Shopify" control — a site with no
 * Shopify can't sync, and showing the button there is misleading (it would
 * just error). Reads the connector status (`?kind=shopify`); a 404 means no
 * integration row exists → not connected. Any error → `false` (fail SAFE: when
 * we cannot confirm a live connection, hide the action rather than offer a
 * sync that can only fail).
 */
export async function getShopifyConnected(
  http: HttpClient,
  site_id: string,
): Promise<boolean> {
  try {
    const res = await http.request<{ status?: unknown }>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/connector?kind=shopify`,
    });
    return res?.status === 'connected';
  } catch {
    return false;
  }
}

/**
 * GET a bounded page of the site's catalog products. Optional server-side
 * `search` (title/item_id/brand substring), `sort` (whitelisted column),
 * and `dir` are passed straight through as query params; the server does
 * the filtering/ordering and echoes the applied values back.
 */
export async function getCatalog(
  http: HttpClient,
  site_id: string,
  opts: {
    limit?: number;
    offset?: number;
    search?: string;
    sort?: CatalogSortColumn;
    dir?: CatalogSortDir;
  } = {},
): Promise<GetCatalogResult> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  // An empty/whitespace search is "no search" — don't send it.
  if (opts.search !== undefined && opts.search.trim().length > 0) {
    params.set('search', opts.search.trim());
  }
  if (opts.sort !== undefined) {
    params.set('sort', opts.sort);
    params.set('dir', opts.dir ?? 'asc');
  }
  const qs = params.toString();
  try {
    const res = await http.request<CatalogWire>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/catalog${qs.length > 0 ? `?${qs}` : ''}`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const page = parseCatalogPage(res);
    if (page === null) {
      return { kind: 'error', message: 'Server returned an unexpected catalog page.' };
    }
    return { kind: 'ok', page };
  } catch (err) {
    return mapReadError(err);
  }
}

export type DownloadFeedResult =
  | { readonly kind: 'ok'; readonly blob: Blob }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

/**
 * Download the site's OpenAI product-feed projection as JSONL — the exact feed
 * that would be published to agents (only search-eligible products; checkout
 * block on checkout-eligible rows). Fetched via the authenticated client (the
 * endpoint is owner/admin + site-scoped), returned as a Blob the caller saves.
 */
export async function downloadCatalogFeed(
  http: HttpClient,
  site_id: string,
): Promise<DownloadFeedResult> {
  if (typeof http.requestBlob !== 'function') {
    return { kind: 'error', message: 'Download is not supported in this context.' };
  }
  try {
    const blob = await http.requestBlob({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/catalog/feed?format=jsonl`,
    });
    return { kind: 'ok', blob };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/**
 * PATCH a manually-managed (CSV / site-scan) catalog product's fields. The
 * body is the full editable set. 409 `source_managed` → the product is owned by
 * a connected cart (edit it there). The caller reloads the list on success.
 */
export async function updateCatalogProduct(
  http: HttpClient,
  site_id: string,
  catalog_product_id: string,
  fields: CatalogProductEdit,
): Promise<UpdateProductResult> {
  try {
    await http.request<unknown>({
      method: 'PATCH',
      path: `/v1/sites/${encodeURIComponent(site_id)}/catalog/products/${encodeURIComponent(catalog_product_id)}`,
      body: fields,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'conflict') return { kind: 'source_managed' };
      if (err.kind === 'bad_request') {
        return err.server_reason !== undefined
          ? { kind: 'bad_request', reason: err.server_reason }
          : { kind: 'bad_request' };
      }
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/**
 * DELETE a catalog product. Any source — a cart-synced product reappears on the
 * next sync. The server responds 204; the client reloads the list.
 */
export async function deleteCatalogProduct(
  http: HttpClient,
  site_id: string,
  catalog_product_id: string,
): Promise<DeleteProductResult> {
  try {
    await http.request<unknown>({
      method: 'DELETE',
      path: `/v1/sites/${encodeURIComponent(site_id)}/catalog/products/${encodeURIComponent(catalog_product_id)}`,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/** GET the site's compliance fields. */
export async function getCompliance(
  http: HttpClient,
  site_id: string,
): Promise<GetComplianceResult> {
  try {
    const res = await http.request<ComplianceWire>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/compliance`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const compliance = parseCompliance(res);
    if (compliance === null) {
      return { kind: 'error', message: 'Server returned an unexpected compliance shape.' };
    }
    return { kind: 'ok', compliance };
  } catch (err) {
    return mapReadError(err);
  }
}

/** PUT a partial compliance update. */
export async function putCompliance(
  http: HttpClient,
  site_id: string,
  update: ComplianceUpdate,
): Promise<PutComplianceResult> {
  try {
    const res = await http.request<ComplianceWire>({
      method: 'PUT',
      path: `/v1/sites/${encodeURIComponent(site_id)}/compliance`,
      body: update,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const compliance = parseCompliance(res);
    if (compliance === null) {
      return { kind: 'error', message: 'Server returned an unexpected compliance shape.' };
    }
    return { kind: 'ok', compliance };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'bad_request') {
        return err.server_reason !== undefined
          ? { kind: 'bad_request', reason: err.server_reason }
          : { kind: 'bad_request' };
      }
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/**
 * GET the site's catalog eligibility (ADR-0103). Returns the current catalog
 * rules, the suggested default rule set, the live counts, and the resolved
 * site context. Read-only; owner/admin (403 → forbidden); site-scoped.
 */
export async function getEligibility(
  http: HttpClient,
  site_id: string,
): Promise<GetEligibilityResult> {
  try {
    const res = await http.request<EligibilityWire>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/catalog/eligibility`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const state = parseEligibility(res);
    if (state === null) {
      return { kind: 'error', message: 'Server returned an unexpected eligibility shape.' };
    }
    return { kind: 'ok', state };
  } catch (err) {
    return mapReadError(err);
  }
}

/**
 * POST a dry-run preview of a candidate catalog rule set over the live
 * catalog. No writes. The `rules` are passed verbatim (typically the
 * `suggestion` from `getEligibility`). 400 → bad_request with the server reason.
 */
export async function previewEligibility(
  http: HttpClient,
  site_id: string,
  rules: readonly Rule[],
): Promise<PreviewEligibilityResult> {
  try {
    const res = await http.request<PreviewWire>({
      method: 'POST',
      path: `/v1/sites/${encodeURIComponent(site_id)}/catalog/eligibility/preview`,
      body: { rules },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const preview = parsePreview(res);
    if (preview === null) {
      return { kind: 'error', message: 'Server returned an unexpected preview shape.' };
    }
    return { kind: 'ok', preview };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'bad_request') {
        return err.server_reason !== undefined
          ? { kind: 'bad_request', reason: err.server_reason }
          : { kind: 'bad_request' };
      }
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/**
 * POST to ACTIVATE a catalog rule set. The merchant's activation IS the
 * recorded decision — this publishes a new policy version server-side and
 * re-derives the eligibility flags. Maps every outcome to a kind; never
 * throws. A 409 → conflict (a concurrent policy change won the race — the
 * view asks the merchant to refresh and retry).
 */
export async function activateEligibility(
  http: HttpClient,
  site_id: string,
  rules: readonly Rule[],
): Promise<ActivateEligibilityResult> {
  try {
    const res = await http.request<ActivationWire>({
      method: 'POST',
      path: `/v1/sites/${encodeURIComponent(site_id)}/catalog/eligibility/activate`,
      body: { rules },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const activation = parseActivation(res);
    if (activation === null) {
      return { kind: 'error', message: 'Server returned an unexpected activation shape.' };
    }
    return { kind: 'ok', activation };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'conflict') return { kind: 'conflict' };
      if (err.kind === 'bad_request') {
        return err.server_reason !== undefined
          ? { kind: 'bad_request', reason: err.server_reason }
          : { kind: 'bad_request' };
      }
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/* ---------- parsing helpers ---------- */

function isFiniteInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v);
}

function parseImportSummary(w: ImportWire): CatalogImportSummary | null {
  if (!isFiniteInt(w.synced) || !isFiniteInt(w.stale) || !isFiniteInt(w.errors)) {
    return null;
  }
  if (typeof w.truncated !== 'boolean') return null;
  // `deduped` is newer; older API builds omit it → default 0.
  const deduped = isFiniteInt(w.deduped) ? w.deduped : 0;
  return { synced: w.synced, stale: w.stale, errors: w.errors, truncated: w.truncated, deduped };
}

function parseCatalogPage(w: CatalogWire): CatalogPage | null {
  if (!Array.isArray(w.products)) return null;
  if (!isFiniteInt(w.limit) || !isFiniteInt(w.offset)) return null;
  if (typeof w.has_more !== 'boolean') return null;
  // Trust the server's CatalogProduct shape (RLS-scoped, route-validated);
  // we only assert the envelope. A row is passed through as-is.
  // `last_synced_at` is a site-level ISO timestamp or null; older API builds
  // omit it entirely (→ null). Any non-string, non-null value is treated as
  // null rather than failing the whole page.
  const last_synced_at =
    typeof w.last_synced_at === 'string' ? w.last_synced_at : null;
  return {
    products: w.products as readonly CatalogProduct[],
    limit: Math.min(w.limit, MAX_PAGE_LIMIT),
    offset: w.offset,
    has_more: w.has_more,
    last_synced_at,
  };
}

function parseCompliance(w: ComplianceWire): SiteComplianceFields | null {
  if (typeof w.site_id !== 'string' || typeof w.account_id !== 'string') return null;
  const out: {
    -readonly [K in keyof SiteComplianceFields]: SiteComplianceFields[K];
  } = { site_id: w.site_id, account_id: w.account_id };
  if (typeof w.seller_tos_url === 'string') out.seller_tos_url = w.seller_tos_url;
  if (typeof w.seller_privacy_policy_url === 'string') {
    out.seller_privacy_policy_url = w.seller_privacy_policy_url;
  }
  if (typeof w.returns_policy === 'string') out.returns_policy = w.returns_policy;
  return out;
}

function parseCounts(w: unknown): EligibilityCounts | null {
  if (w === null || typeof w !== 'object') return null;
  const c = w as EligibilityCountsWire;
  if (!isFiniteInt(c.total) || !isFiniteInt(c.search_eligible) || !isFiniteInt(c.checkout_eligible)) {
    return null;
  }
  return {
    total: c.total,
    search_eligible: c.search_eligible,
    checkout_eligible: c.checkout_eligible,
  };
}

function parseEligibility(w: EligibilityWire): EligibilityState | null {
  if (!Array.isArray(w.current_rules) || !Array.isArray(w.suggestion)) return null;
  const counts = parseCounts(w.counts);
  if (counts === null) return null;
  if (w.site_context === null || typeof w.site_context !== 'object') return null;
  const ctx = w.site_context as SiteContextWire;
  if (
    typeof ctx.has_settlement_rail !== 'boolean'
    || typeof ctx.has_compliance_fields !== 'boolean'
  ) {
    return null;
  }
  // The rule arrays are trusted as-is (route-validated, RLS-scoped) and
  // round-tripped opaquely back into preview/activate; we only assert the
  // envelope is an array.
  return {
    current_rules: w.current_rules as readonly Rule[],
    suggestion: w.suggestion as readonly Rule[],
    // ADR-0112 §4 — tolerate older servers that omit checkout_suggestion.
    checkout_suggestion: Array.isArray(w.checkout_suggestion)
      ? (w.checkout_suggestion as readonly Rule[])
      : [],
    counts,
    site_context: {
      has_settlement_rail: ctx.has_settlement_rail,
      has_compliance_fields: ctx.has_compliance_fields,
    },
  };
}

function parsePreview(w: PreviewWire): EligibilityPreview | null {
  if (
    !isFiniteInt(w.total)
    || !isFiniteInt(w.would_search_eligible)
    || !isFiniteInt(w.would_checkout_eligible)
  ) {
    return null;
  }
  return {
    total: w.total,
    would_search_eligible: w.would_search_eligible,
    would_checkout_eligible: w.would_checkout_eligible,
  };
}

function parseActivation(w: ActivationWire): EligibilityActivation | null {
  if (typeof w.policy_version !== 'string' || w.policy_version.length === 0) return null;
  const counts = parseCounts(w.counts);
  if (counts === null) return null;
  return { policy_version: w.policy_version, counts };
}

function mapReadError(err: unknown): { kind: 'forbidden' } | { kind: 'not_found' } | { kind: 'error'; message: string } {
  if (err instanceof ApiError) {
    if (err.kind === 'forbidden') return { kind: 'forbidden' };
    if (err.kind === 'not_found') return { kind: 'not_found' };
    return { kind: 'error', message: explainApiError(err) };
  }
  return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'The catalog service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
